/**
 * Retry for the Download Sessions panel (EN-1506).
 *
 * One "Retry timed out (N)" button retries the timed-out sessions in the panel
 * through `POST /download-sessions/retry`. The WebApi looks up each session's
 * original download (UID, FileType, range), checks the caller's group, queues
 * it again under a per-UID lock (one request per original, covering just the
 * timed-out UIDs) and returns one result per session. Because the request
 * lives on the server, this works for any user who can see the session,
 * including one that timed out after a reload.
 *
 * Only sessions the WebApi says a retry can restart are counted:
 * `POST /download-sessions/retryable` returns a `retryable` flag per session
 * (false for Iridium devices, DBLAR broadcasts, ID/IDWTR, missing or
 * superseded requests and other groups' sessions). Ids go to the WebApi in
 * chunks of at most MAX_SESSIONS_PER_REQUEST.
 *
 * This module has no service or store imports so it can be unit tested.
 */

import {
  NO_PERMISSION_MESSAGE,
  isForbiddenError,
} from './DownloadSessionAccess';

/** Session state for a timed-out download. */
export const TIMED_OUT_STATE = 'X';

/** The WebApi accepts at most this many ids per request. */
export const MAX_SESSIONS_PER_REQUEST = 100;

/** Shown when the retry request itself failed (nothing was retried). */
export const RETRY_REQUEST_FAILED_MESSAGE =
  'Could not retry the downloads. Please try again.';

/** Statuses returned per session by the WebApi. */
export type RetryDownloadSessionStatus =
  | 'Queued'
  | 'Retryable'
  | 'NotFound'
  | 'NotTimedOut'
  | 'MissingCommand'
  | 'NotRetryable'
  | 'Superseded'
  | 'Duplicate'
  | 'Failed';

export interface RetryDownloadSessionResult {
  downloadSessionId: number;
  status: RetryDownloadSessionStatus | string;
  /** True when a retry could (still) restart this session. */
  retryable?: boolean | null;
  message?: string | null;
  uid?: number | null;
  fileType?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  newCommandId?: number | null;
  retryGroup?: number | null;
}

export interface RetryDownloadSessionsResponse {
  results: RetryDownloadSessionResult[];
  groups?: {
    retryGroup: number;
    command: string;
    fileType: number | null;
    startDate: string;
    endDate: string;
    uids: number[];
  }[];
  queuedCount: number;
}

/** The parts of a download session the retry button needs. */
export interface RetrySessionInput {
  downloadSessionId: number;
  uid: number;
  fileType: number;
  state: string;
  startDate: string;
  endDate: string;
  /** From the WebApi; unset until checked. */
  retryable?: boolean | null;
  /** EN-1517: Iridium downloads are never retried. */
  isIridium?: boolean;
}

/** A new pending card to show for a retry the WebApi accepted. */
export interface RetryPendingSession {
  uid: number;
  fileType: number;
  startDate: string;
  endDate: string;
  /**
   * EN-1517: the timed-out card this retry replaces. The store no longer
   * drops every card for the UID when a new download is requested, so the
   * replaced card is named explicitly.
   */
  replacesSessionId?: number;
}

/** A timed-out card whose retry was not accepted, and why. */
export interface RetryFailure {
  downloadSessionId: number;
  message: string;
  /** Whether the button should still offer this session. */
  retryable: boolean;
}

/** Short user-facing text for a status the WebApi did not queue. */
const STATUS_MESSAGES: Record<string, string> = {
  NotFound: 'Retry failed: download not found.',
  NotTimedOut: 'Retry skipped: this download is not timed out.',
  MissingCommand: 'Retry failed: the original request no longer exists.',
  NotRetryable: 'Retry not available for this type of download.',
  Superseded:
    'Retry skipped: a newer download for this UID is running or queued.',
  Duplicate: 'Retry skipped: another download for this UID was retried.',
  Failed: 'Retry failed: the download could not be queued.',
};

const isRealTimedOut = (s: RetrySessionInput) =>
  s.downloadSessionId > 0 && s.state === TIMED_OUT_STATE && !s.isIridium;

/**
 * Ids of the real (not pending) timed-out sessions the WebApi flagged as
 * retryable, in panel order. These are what the button counts and sends.
 */
export function timedOutSessionIds(
  sessions: readonly RetrySessionInput[] | null | undefined
): number[] {
  return (sessions ?? [])
    .filter((s) => isRealTimedOut(s) && s.retryable === true)
    .map((s) => s.downloadSessionId);
}

/** Ids of real timed-out sessions not yet checked with the WebApi. */
export function uncheckedTimedOutSessionIds(
  sessions: readonly RetrySessionInput[] | null | undefined
): number[] {
  return (sessions ?? [])
    .filter((s) => isRealTimedOut(s) && s.retryable == null)
    .map((s) => s.downloadSessionId);
}

/** Splits ids into chunks the WebApi accepts. */
export function chunkIds(
  ids: readonly number[],
  size = MAX_SESSIONS_PER_REQUEST
): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += size)
    chunks.push(ids.slice(i, i + size));
  return chunks;
}

/**
 * Whether the panel shows "Retry timed out": admins only (the same
 * canManageDownloads gate as Cancel and queueing) and at least one session
 * the WebApi flagged retryable.
 */
export function showsRetryButton(
  timedOutCount: number,
  access: { canManageDownloads: boolean }
): boolean {
  return access.canManageDownloads && timedOutCount > 0;
}

/** Label of the panel's retry button. */
export function retryButtonLabel(count: number): string {
  return `Retry timed out (${count})`;
}

/** The message shown on a card whose retry was not accepted. */
export function retryFailureMessage(
  result: RetryDownloadSessionResult
): string {
  return STATUS_MESSAGES[result.status] ?? 'Retry failed.';
}

/**
 * Asks the WebApi, in chunks, which sessions a retry can restart. Returns the
 * flag for every id of each chunk that answered (an id missing from an answer
 * is not retryable) and the ids whose chunk failed (left unchecked). EN-1517:
 * a 403 (retry is admin-only) answers its chunk as not retryable, so it is
 * never asked again.
 */
export async function checkRetryableSessions(
  ids: readonly number[],
  check: (ids: number[]) => Promise<RetryDownloadSessionsResponse>
): Promise<{ retryable: Map<number, boolean>; failedIds: number[] }> {
  const retryable = new Map<number, boolean>();
  const failedIds: number[] = [];
  for (const chunk of chunkIds(ids)) {
    let response: RetryDownloadSessionsResponse;
    try {
      response = await check(chunk);
    } catch (error) {
      if (isForbiddenError(error)) {
        for (const id of chunk) retryable.set(id, false);
      } else {
        failedIds.push(...chunk);
      }
      continue;
    }
    const byId = new Map<number, boolean>();
    for (const r of response?.results ?? [])
      byId.set(r.downloadSessionId, r.retryable === true);
    for (const id of chunk) retryable.set(id, byId.get(id) ?? false);
  }
  return { retryable, failedIds };
}

/**
 * Checks the ids not in `checkedIds` yet (see checkRetryableSessions). The
 * ids are added to `checkedIds` while the request runs, so a second trigger
 * does not ask again, and removed again for every chunk that failed (or for
 * all of them if the call throws), so the next check asks about them again
 * instead of leaving their Retry button hidden until the page is reloaded.
 */
export async function checkNewRetryableSessions(
  ids: readonly number[],
  checkedIds: Set<number>,
  check: (ids: number[]) => Promise<RetryDownloadSessionsResponse>
): Promise<{ retryable: Map<number, boolean>; failedIds: number[] }> {
  const fresh = ids.filter((id) => !checkedIds.has(id));
  if (fresh.length === 0) return { retryable: new Map(), failedIds: [] };
  fresh.forEach((id) => checkedIds.add(id));
  let result: { retryable: Map<number, boolean>; failedIds: number[] };
  try {
    result = await checkRetryableSessions(fresh, check);
  } catch (error) {
    fresh.forEach((id) => checkedIds.delete(id));
    throw error;
  }
  result.failedIds.forEach((id) => checkedIds.delete(id));
  return result;
}

/**
 * Splits the WebApi response into new pending cards (accepted retries, using
 * the server's UID, FileType and range, falling back to the card's own) and
 * failures to show on the cards that stay. A requested id missing from the
 * response counts as failed and stays retryable.
 */
export function applyRetryResults(
  requested: readonly RetrySessionInput[],
  response: RetryDownloadSessionsResponse
): { pending: RetryPendingSession[]; failures: RetryFailure[] } {
  const byId = new Map<number, RetryDownloadSessionResult>();
  for (const r of response?.results ?? []) byId.set(r.downloadSessionId, r);

  const pending: RetryPendingSession[] = [];
  const failures: RetryFailure[] = [];
  for (const session of requested) {
    const result = byId.get(session.downloadSessionId);
    if (result && result.status === 'Queued') {
      pending.push({
        uid: result.uid ?? session.uid,
        fileType: result.fileType ?? session.fileType,
        startDate: result.startDate ?? session.startDate,
        endDate: result.endDate ?? session.endDate,
        replacesSessionId: session.downloadSessionId,
      });
    } else if (result) {
      failures.push({
        downloadSessionId: session.downloadSessionId,
        message: retryFailureMessage(result),
        retryable: result.retryable ?? result.status === 'Failed',
      });
    } else {
      failures.push({
        downloadSessionId: session.downloadSessionId,
        message: 'Retry failed.',
        retryable: true,
      });
    }
  }
  return { pending, failures };
}

/**
 * Retries every retryable timed-out session in `sessions`, one WebApi call per
 * chunk of MAX_SESSIONS_PER_REQUEST ids. Rejects only when every call failed
 * (nothing was retried); ids of a failed chunk otherwise count as failed.
 */
export async function retryTimedOutSessions(
  sessions: readonly RetrySessionInput[],
  retry: (ids: number[]) => Promise<RetryDownloadSessionsResponse>
): Promise<{
  requested: number;
  pending: RetryPendingSession[];
  failures: RetryFailure[];
}> {
  const ids = timedOutSessionIds(sessions);
  if (ids.length === 0) return { requested: 0, pending: [], failures: [] };
  const wanted = new Set(ids);
  const requested = sessions.filter((s) => wanted.has(s.downloadSessionId));

  const results: RetryDownloadSessionResult[] = [];
  let firstError: unknown;
  let succeeded = 0;
  for (const chunk of chunkIds(ids)) {
    try {
      const response = await retry(chunk);
      results.push(...(response?.results ?? []));
      succeeded++;
    } catch (error) {
      firstError ??= error;
    }
  }
  if (succeeded === 0) throw firstError;

  const queuedCount = results.filter((r) => r.status === 'Queued').length;
  return {
    requested: ids.length,
    ...applyRetryResults(requested, { results, queuedCount }),
  };
}

/** What the panel's retry button needs from the app. */
export interface TimedOutRetrierDeps {
  getSessions: () => readonly RetrySessionInput[];
  retry: (ids: number[]) => Promise<RetryDownloadSessionsResponse>;
  setRetryError: (sessionId: number, message: string | undefined) => void;
  setRetryable: (sessionId: number, retryable: boolean) => void;
  createPending: (session: RetryPendingSession) => void;
  notify: (message: string) => void;
  setBusy: (busy: boolean) => void;
  logError?: (message: string, error: unknown) => void;
}

/**
 * The retry button's click handler, outside the component so it can be
 * tested: ignores clicks while a retry is running, clears old errors, retries,
 * then shows pending cards, per-card errors and one notification. A failed
 * request shows a fixed message, never the raw error. EN-1517: a 403 (retry is
 * admin-only) shows NO_PERMISSION_MESSAGE, is not tried again, and leaves the
 * cards unchanged (old errors are cleared only once the WebApi answered).
 */
export function createTimedOutRetrier(deps: TimedOutRetrierDeps): {
  run: () => Promise<void>;
  isBusy: () => boolean;
} {
  let busy = false;
  const run = async () => {
    if (busy) return;
    const sessions = deps
      .getSessions()
      .filter((s) => isRealTimedOut(s) && s.retryable === true);
    if (sessions.length === 0) return;

    busy = true;
    deps.setBusy(true);
    try {
      const { requested, pending, failures } = await retryTimedOutSessions(
        sessions,
        deps.retry
      );
      for (const s of sessions)
        deps.setRetryError(s.downloadSessionId, undefined);
      for (const f of failures) {
        deps.setRetryError(f.downloadSessionId, f.message);
        deps.setRetryable(f.downloadSessionId, f.retryable);
      }
      for (const p of pending) deps.createPending(p);
      deps.notify(
        failures.length === 0
          ? `Retrying ${pending.length} download${
              pending.length === 1 ? '' : 's'
            }`
          : `Retrying ${pending.length} of ${requested} downloads`
      );
    } catch (error) {
      deps.logError?.('Failed to retry timed-out downloads', error);
      deps.notify(
        isForbiddenError(error)
          ? NO_PERMISSION_MESSAGE
          : RETRY_REQUEST_FAILED_MESSAGE
      );
    } finally {
      busy = false;
      deps.setBusy(false);
    }
  };
  return { run, isBusy: () => busy };
}
